import { useMemo } from 'react';
import { useMlsById } from 'src/common/hooks/useMlsById/useMlsById';
import { formatAddress } from '@/common/utils/address';
import { getAllNames } from '@/common/utils/person';
import { getTransactionStatusList } from '@/views/Interactions/helpers/InteractionStatus.helper';
import { type CoreRequest, useCore } from '$api-clients/hooks';

type Listing = CoreRequest<'/listings'>['Data']['results'][number];
type ListingStatus = Listing['status'];
type Contact = Listing['contacts'][number];
type ImageFile =
  CoreRequest<'/interactions/{interactionId}/files/images'>['Data'][number];

const getStatusLabel = (status: ListingStatus | undefined): string => {
  if (!status) return '';
  return (
    getTransactionStatusList('listing').find((s) => s.value === status)
      ?.label ?? ''
  );
};

export type ListingSummaryResult = {
  coverPhotoUrl: string | null;
  formattedFullAddress: string;
  formattedStreetAddress: string;
  formattedCityStateZip: string;
  state: string | null;
  mlsNumber: string | null;
  mlsVendorName: string | null;
  formattedSellers: string;
  interactionStatus: string;
};

export type UseListingSummaryResult = {
  listing: ListingSummaryResult | undefined;
  loading: boolean;
};

export type UseListingSummaryOptions = {
  listingId: string | undefined;
  skipMlsInformation?: boolean;
  skipCoverPhoto?: boolean;
};

export const useListingSummary = ({
  listingId,
  skipMlsInformation = false,
  skipCoverPhoto = false,
}: UseListingSummaryOptions): UseListingSummaryResult => {
  const { data, isLoading: listingLoading } = useCore(
    '/listings',
    listingId
      ? { params: { query: { ids: listingId, includeContacts: true } } }
      : null,
  );
  const listing = data?.results?.[0];

  const coverPhotoFileId = skipCoverPhoto
    ? null
    : (listing?.coverPhotoFileId ?? null);
  const { data: images, isLoading: imagesLoading } = useCore(
    '/interactions/{interactionId}/files/images',
    listingId && coverPhotoFileId
      ? { params: { path: { interactionId: listingId } } }
      : null,
  );

  // Vendor name resolves by id from identity's catalog — one lookup that
  // works for any saved id, in-state or not, including retired entries.
  const mlsServiceId = skipMlsInformation
    ? undefined
    : (listing?.property?.mlsServiceId ?? undefined);
  const { mlsVendorOption: mlsVendor, loading: mlsLoading } = useMlsById(
    mlsServiceId ?? null,
  );
  const mlsVendorName = mlsVendor?.name ?? null;

  const loading = listingLoading || imagesLoading || mlsLoading;

  return useMemo(() => {
    if (!listingId || loading || !listing) {
      return { listing: undefined, loading };
    }

    const property = listing.property;

    const resolveCoverPhotoUrl = (): string | null => {
      if (skipCoverPhoto) return null;
      if (coverPhotoFileId) {
        return (
          (images ?? []).find(
            (image: ImageFile) => image.id === coverPhotoFileId,
          )?.file?.downloadUrl ?? null
        );
      }
      return listing.photos?.[0] ?? null;
    };

    return {
      listing: {
        coverPhotoUrl: resolveCoverPhotoUrl(),
        formattedFullAddress: formatAddress(property, { includeStreet2: true }),
        formattedStreetAddress: formatAddress(
          { street1: property?.street1, street2: property?.street2 },
          { includeStreet2: true },
        ),
        formattedCityStateZip: formatAddress({
          city: property?.city,
          state: property?.state,
          zip: property?.zip,
        }),
        state: property?.state ?? null,
        mlsNumber: skipMlsInformation ? null : property?.mlsNumber || null,
        mlsVendorName,
        formattedSellers: getAllNames(
          listing.contacts?.filter(
            (contact: Contact) => contact.type === 'client',
          ),
        ),
        interactionStatus: getStatusLabel(listing.status),
      },
      loading,
    };
  }, [
    listing,
    images,
    coverPhotoFileId,
    skipCoverPhoto,
    skipMlsInformation,
    mlsVendorName,
    listingId,
    loading,
  ]);
};
